import { streamLog } from './streamLogger';
interface StreamCaptureEvent {
  timestamp: number;
  eventType: string;
  rawData: string;
  parsedData?: any;
  processingTime: number;
}

interface StreamCaptureMetadata {
  timestamp: string;
  startTime: number;
  endTime?: number;
  url: string;
  method: string;
  requestHeaders: Record<string, string>;
  requestBody: any;
}

interface StreamCaptureSummary {
  totalEvents: number;
  eventTypes: Record<string, number>;
  totalBytes: number;
  duration: number;
  errors: string[];
}

interface StreamCaptureData {
  metadata: StreamCaptureMetadata;
  events: StreamCaptureEvent[];
  summary: StreamCaptureSummary;
  finalContent: {
    accumulated: string;
    contentLength: number;
  };
}

export class StreamDataCapture {
  private captureData: StreamCaptureData;
  private gameId: string;
  private startTime: number;

  constructor(url: string, init: RequestInit, gameId: string) {
    this.gameId = gameId;
    this.startTime = Date.now();
    
    this.captureData = {
      metadata: {
        timestamp: new Date().toISOString(),
        startTime: this.startTime,
        url,
        method: init.method || 'GET',
        requestHeaders: { ...(init.headers as Record<string, string>) },
        requestBody: init.body ? JSON.parse(init.body as string) : null,
      },
      events: [],
      summary: {
        totalEvents: 0,
        eventTypes: {},
        totalBytes: 0,
        duration: 0,
        errors: [],
      },
      finalContent: {
        accumulated: '',
        contentLength: 0,
      },
    };

    try { streamLog.init('🎥 Stream capture started for game:', gameId); } catch (_) {}
  }

  captureEvent(eventType: string, rawData: string, parsedData?: any): void {
    const processingStart = performance.now();
    
    try {
      const event: StreamCaptureEvent = {
        timestamp: Date.now() - this.startTime, // Relative to stream start
        eventType,
        rawData,
        parsedData,
        processingTime: performance.now() - processingStart,
      };

      this.captureData.events.push(event);
      this.captureData.summary.totalEvents++;
      this.captureData.summary.totalBytes += rawData.length;
      
      // Track event type counts
      if (!this.captureData.summary.eventTypes[eventType]) {
        this.captureData.summary.eventTypes[eventType] = 0;
      }
      this.captureData.summary.eventTypes[eventType]++;

    } catch (error) {
      const errorMsg = `Failed to capture event ${eventType}: ${error instanceof Error ? error.message : String(error)}`;
      this.captureData.summary.errors.push(errorMsg);
      try { streamLog.error('🚨 Stream capture error:', errorMsg); } catch (_) {}
    }
  }

  captureContent(content: string): void {
    this.captureData.finalContent.accumulated += content;
    this.captureData.finalContent.contentLength = this.captureData.finalContent.accumulated.length;
  }

  captureError(error: string): void {
    this.captureData.summary.errors.push(error);
  }

  finalize(accumulated?: string): void {
    this.captureData.metadata.endTime = Date.now();
    this.captureData.summary.duration = this.captureData.metadata.endTime - this.startTime;

    // Use provided accumulated content if available (from orchestrator)
    // This ensures we capture the final, complete content
    if (accumulated !== undefined) {
      this.captureData.finalContent.accumulated = accumulated;
      this.captureData.finalContent.contentLength = accumulated.length;
    }

    try { streamLog.wrap('🎥 Stream capture completed:', {
      events: this.captureData.summary.totalEvents,
      types: Object.keys(this.captureData.summary.eventTypes).length,
      bytes: this.captureData.summary.totalBytes,
      duration: this.captureData.summary.duration,
      errors: this.captureData.summary.errors.length,
    }); } catch (_) {}

    // Log the entire final response to console
    try { streamLog.debug('📋 COMPLETE STREAM CAPTURE DATA:', this.captureData); } catch (_) {}

    this.downloadCapture();
  }

  private downloadCapture(): void {
    try {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19); // YYYY-MM-DD-HH-mm-ss
      const filename = `stream-${this.gameId}-${timestamp}.json`;
      
      const jsonString = JSON.stringify(this.captureData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);

      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      try { streamLog.wrap('📁 Stream data downloaded:', filename); } catch (_) {}
    } catch (error) {
      try { streamLog.error('🚨 Failed to download stream capture:', error); } catch (_) {}
      
      // Fallback: log to console for manual copy
      try { streamLog.warn('📋 Stream capture data (copy manually):', JSON.stringify(this.captureData, null, 2)); } catch (_) {}
    }
  }

  // Public method to get current capture data (for debugging)
  getCaptureData(): StreamCaptureData {
    return { ...this.captureData };
  }
};
